// Step 2 — choose ticket types and quantities. Available types get a big
// stepper; unavailable ones show as a greyed card with the reason. A pinned
// bottom bar carries the running total + Continue.

import type { TicketTypeForKiosk } from "@entradart/kiosk-shared/api";
import { Banner } from "../components/Banner";
import { Button } from "../components/Button";
import { ArrowLeft, CalendarGlyph } from "../components/Illustrations";
import { KioskShell } from "../components/KioskShell";
import { Stepper } from "../components/Stepper";
import { type Translate, useLang } from "../i18n/LangContext";
import { formatCLP, formatDateLong, formatPrice } from "../lib/format";
import { totalAmount, totalTickets } from "../state/reducer";
import type { KioskState } from "../state/types";
import type { Dispatch } from "./shared";

type SelectTickets = Extract<KioskState, { phase: "select-tickets" }>;

function ticketCountLabel(count: number, t: Translate): string {
  return count === 1
    ? t("ticketCountOne", { n: count })
    : t("ticketCountMany", { n: count });
}

export function TicketsScreen({
  state,
  dispatch,
}: {
  state: SelectTickets;
  dispatch: Dispatch;
}) {
  const { t, locale } = useLang();
  const count = totalTickets(state.quantities);
  const total = totalAmount(state);
  const hasAvailable = state.ticketTypes.some((tt) => tt.available);

  return (
    <KioskShell step={2}>
      <div className="screen screen--wide">
        <h1 className="screen__title">{t("ticketsTitle")}</h1>
        <span className="chip with-icon">
          <CalendarGlyph size={16} />
          {formatDateLong(state.date, locale)}
        </span>
        {hasAvailable ? null : (
          <Banner variant="warn">{t("ticketsEmpty")}</Banner>
        )}
        <div className="ticket-list">
          {state.ticketTypes.map((tt) => (
            <TicketRow
              key={tt.id}
              locale={locale}
              onChange={(quantity) =>
                dispatch({
                  type: "set-quantity",
                  ticketTypeId: tt.id,
                  quantity,
                })
              }
              quantity={state.quantities[tt.id] ?? 0}
              t={t}
              ticketType={tt}
            />
          ))}
        </div>
      </div>
      <div className="bottom-bar">
        <div className="bottom-bar__summary">
          <span className="bottom-bar__total">{formatCLP(total, locale)}</span>
          <span className="bottom-bar__count">
            {ticketCountLabel(count, t)}
          </span>
        </div>
        <div className="bottom-bar__actions">
          <Button
            icon={<ArrowLeft />}
            onClick={() => dispatch({ type: "go-back" })}
            variant="ghost"
          >
            {t("back")}
          </Button>
          <Button
            disabled={count === 0}
            onClick={() => dispatch({ type: "go-to-review" })}
            size="lg"
          >
            {t("continue")}
          </Button>
        </div>
      </div>
    </KioskShell>
  );
}

function TicketRow({
  ticketType,
  quantity,
  onChange,
  t,
  locale,
}: {
  ticketType: TicketTypeForKiosk;
  quantity: number;
  onChange: (next: number) => void;
  t: Translate;
  locale: string;
}) {
  if (!ticketType.available) {
    return (
      <div className="card card--row card--muted">
        <div className="card__main">
          <span className="card__title">{ticketType.name}</span>
          <span className="card__desc">
            {ticketType.unavailableReason ?? t("unavailable")}
          </span>
        </div>
        <span className="chip">{t("unavailable")}</span>
      </div>
    );
  }
  const max = ticketType.maxPerOrder ?? 99;
  return (
    <div className="card card--row">
      <div className="card__main">
        <span className="card__title">{ticketType.name}</span>
        {ticketType.description ? (
          <span className="card__desc">{ticketType.description}</span>
        ) : null}
        <span className="chip">
          {formatPrice(ticketType.priceCLP, locale, t("free"))}
        </span>
      </div>
      <Stepper
        decLabel={t("remove")}
        incLabel={t("add")}
        max={max}
        onChange={onChange}
        value={quantity}
      />
    </div>
  );
}
